import { Star } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { DashboardStaffLeaderboardEntry } from "./types";
import { formatCurrencyMinor, initialsOf } from "./utils";

export interface DashboardStaffLeaderboardProps {
  entries: DashboardStaffLeaderboardEntry[];
  title: string;
  completedLabel: string;
  revenueLabel: string;
  ratingLabel: string;
  emptyLabel: string;
  className?: string;
}

export function DashboardStaffLeaderboard({
  entries,
  title,
  completedLabel,
  revenueLabel,
  ratingLabel,
  emptyLabel,
  className,
}: DashboardStaffLeaderboardProps) {
  return (
    <section
      className={cn(
        "flex flex-col rounded-biz-card border border-biz-border bg-biz-surface-elevated biz-shadow-sm",
        className,
      )}
      aria-labelledby="dash-staff-title"
    >
      <header className="border-b border-biz-border px-4 py-3">
        <h3 id="dash-staff-title" className="text-sm font-semibold text-biz-foreground">
          {title}
        </h3>
      </header>
      {entries.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-biz-foreground-muted">{emptyLabel}</div>
      ) : (
        <ol className="divide-y divide-biz-border" role="list">
          {entries.map((e, i) => (
            <li key={e.staffId} className="flex items-center gap-3 px-4 py-2.5">
              <div className="font-mono-num w-5 text-center text-xs font-semibold text-biz-foreground-muted">
                {i + 1}
              </div>
              <div className="relative h-9 w-9 flex-shrink-0">
                {e.avatarUrl ? (
                  <img
                    src={e.avatarUrl}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-biz-primary-subtle text-xs font-semibold text-biz-primary-subtle-fg">
                    {initialsOf(e.staffName)}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-biz-foreground">
                  {e.staffName}
                </div>
                <div className="truncate text-[11px] text-biz-foreground-muted">{e.role}</div>
              </div>
              <div className="hidden flex-col items-end sm:flex">
                <div className="font-mono-num text-sm font-semibold text-biz-foreground">
                  {formatCurrencyMinor(e.revenueMinor)}
                </div>
                <div className="text-[11px] text-biz-foreground-muted">
                  <span className="font-mono-num">{e.completedCount}</span> {completedLabel}
                </div>
              </div>
              <div className="flex items-center gap-1 rounded-full border border-biz-border bg-biz-muted/50 px-2 py-0.5">
                <Star className="h-3 w-3 text-biz-accent" weight="fill" aria-hidden />
                <span className="font-mono-num text-xs font-semibold text-biz-foreground">
                  {e.avgRating.toFixed(1)}
                </span>
                <span className="sr-only"> {ratingLabel}</span>
              </div>
              <div className="sr-only">
                {formatCurrencyMinor(e.revenueMinor)} {revenueLabel}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
